import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/gql'
import { ApprovalApprovableState, ApprovalTrigger } from '@/gql/graphql'

export const useHireApprovalStatuses = (hireId: string) => {
  const { result, loading } = useQuery(
    graphql(`
      query HireApprovalStatus($id: ID!) {
        hire(id: $id) {
          id
          createdState: currentApprovalState(trigger: HIRE_CREATED) {
            id
            state
            createdAt
          }

          updatedState: currentApprovalState(trigger: HIRE_CHANGED) {
            id
            state
            createdAt
          }

          classificationState: currentApprovalState(trigger: CLASSIFICATION_CREATED) {
            id
            state
            createdAt
          }
        }
      }
    `),
    {
      id: hireId,
    },
    {
      clientId: 'publicApi',
    },
  )

  const created = computed(
    () =>
      !!result.value?.hire?.createdState?.state &&
      result.value?.hire?.createdState?.state !== ApprovalApprovableState.Approved, // We don't care if we have been approved
  )
  const updated = computed(
    () =>
      !!result.value?.hire?.updatedState?.state &&
      result.value?.hire?.updatedState?.state !== ApprovalApprovableState.Approved, // We don't care if we have been approved
  )

  const mostRecentApproval = computed(() => {
    const hire = result.value?.hire
    if (!hire) return null

    const states = [
      hire.createdState && { trigger: ApprovalTrigger.HireCreated, ...hire.createdState },
      hire.updatedState && { trigger: ApprovalTrigger.HireChanged, ...hire.updatedState },
      hire.classificationState && {
        trigger: ApprovalTrigger.ClassificationCreated,
        ...hire.classificationState,
      },
    ].filter(Boolean) as {
      trigger: string
      state: ApprovalApprovableState
      createdAt: string
    }[]

    return (
      states.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ??
      null
    )
  })

  const approvalTrigger = computed(() => mostRecentApproval.value?.trigger ?? null)
  const approvalState = computed(() => mostRecentApproval.value?.state ?? null)

  const rejectedApproval = computed(
    () =>
      approvalState.value === ApprovalApprovableState.Rejected ||
      approvalState.value === ApprovalApprovableState.Cancelled,
  )

  const requiresApproval = computed(() => approvalState.value === ApprovalApprovableState.Requested)

  const hasApprovals = computed(() => rejectedApproval.value || requiresApproval.value)

  return {
    approvalTrigger,
    approvalState,
    rejectedApproval,
    requiresApproval,
    hasApprovals,
    loading,
    created,
    updated,
  }
}
